import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { AdminPayoutRequest, AdminPayoutRequestsQueryParams } from "@shopexperts/legacy-api";

interface UseAdminPayoutRequestsOptions {
  filters?: MaybeRef<AdminPayoutRequestsQueryParams>;
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

export function useAdminPayoutRequests(options: UseAdminPayoutRequestsOptions = {}) {
  const api = useApi();
  const perPage = options.perPage ?? 20;
  const filters = computed(() => unref(options.filters) ?? {});
  const enabled = computed(() => unref(options.enabled) ?? true);

  const query = useInfiniteQuery({
    queryKey: computed(() => ["admin", "payout-requests", { ...filters.value, perPage }]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.wallet.listPayoutRequests({
        query: {
          ...filters.value,
          per_page: perPage,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const payoutRequests = computed<AdminPayoutRequest[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    payoutRequests,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
